import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { GLOBAL_BACKEND_URL } from '../config';

export const BASE_URL = GLOBAL_BACKEND_URL;

// Authenticated call to the backend (JSON in, JSON out)
export function useApi() {
  const { user } = useAuth();
  return useCallback(async (path, options = {}) => {
    const token = await user.getIdToken();
    const headers = { Authorization: `Bearer ${token}` };
    if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Error ${response.status}`);
    return data;
  }, [user]);
}

// Authenticated file download (CSV export)
export function useDownload() {
  const { user } = useAuth();
  return useCallback(async (path, filename) => {
    const token = await user.getIdToken();
    const response = await fetch(`${BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [user]);
}

export const toQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i < 2 ? 0 : 1)} ${units[i]}`;
};

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-PE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const mapUrl = (gps) =>
  gps ? `https://www.google.com/maps/search/?api=1&query=${gps.latitude},${gps.longitude}` : null;

export const FILTER_LABELS = {
  q: 'Búsqueda',
  region: 'Región',
  user: 'Usuario',
  folder: 'Folder',
  date_from: 'Desde',
  date_to: 'Hasta',
  has_gps: 'GPS',
};

export const AI_STATUS = {
  completed: { label: 'Transcrita', tone: 'success' },
  processing: { label: 'Procesando', tone: 'warning' },
  failed: { label: 'Error', tone: 'danger' },
};
